import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft, SkipForward } from 'lucide-react';
import { Button } from './ui/button';
import { useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';

// Tour Context
const TourContext = createContext(null);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// Tour steps for different pages
const TOUR_STEPS = {
  global: [
    {
      target: null,
      title: 'Welcome to your Workspace!',
      content: 'Let us show you around your new Business Service Center. This quick tour will help you get started.',
      placement: 'center',
    },
    {
      target: '[data-tour="nav-done-for-you"]',
      title: 'Done For You (DFY)',
      content: 'Submit projects and our team handles everything. Perfect for busy business owners who want results without the work!',
      placement: 'bottom',
    },
    {
      target: '[data-tour="nav-done-with-you"]',
      title: 'Done With You (DWY)',
      content: 'Book expert sessions for hands-on collaboration and learning. Get personalized guidance from our specialists.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="nav-diy"]',
      title: 'Do It Yourself (DIY)',
      content: 'Access 30+ AI tools to create content, images, and more on your own. Powered by cutting-edge AI technology.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="nav-ranking"]',
      title: 'Ranking System',
      content: 'Manage your SEO & PPC campaigns with our comprehensive toolkit. Track rankings, manage keywords, and monitor performance.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="nav-publish"]',
      title: 'Publish',
      content: 'Create and distribute content across multiple channels from one place. Schedule posts and manage your social presence.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="nav-affiliate"]',
      title: 'Affiliate Program',
      content: 'Promote businesses and earn commissions! Browse the marketplace, share your unique links, and track your earnings.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="nav-agent"]',
      title: 'AGI Workhorse™',
      content: 'Your execution-driven AI system that performs real business tasks directly inside your CRM, Workspace, and Directory. It doesn\'t just suggest - it does it!',
      placement: 'bottom',
    },
    {
      target: '[data-tour="ai-concierge"]',
      title: 'AI Concierge',
      content: 'Your personal AI assistant! Ask questions, get recommendations, and find the right services for your business needs.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="notification-bell"]',
      title: 'Notifications',
      content: 'Stay updated on order status, proposals, team activities, and important alerts. Never miss an important update!',
      placement: 'bottom',
    },
    {
      target: '[data-tour="user-menu"]',
      title: 'Your Account',
      content: 'Access your profile, settings, billing, team management, and more from your account menu.',
      placement: 'bottom',
    },
    {
      target: null,
      title: "You're All Set! 🚀",
      content: 'Click the AI Concierge button anytime to restart this tour or get help. Now go explore and grow your business!',
      placement: 'center',
    },
  ],
  dashboard: [
    {
      target: '[data-tour="quick-links"]',
      title: 'Quick Links',
      content: 'Instant access to our main services: Done For You, Done With You, Do It Yourself, and AI Knowledge.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="co-op-balance"]',
      title: 'Co-Op Balance',
      content: 'Your available credits. Every new account receives $120,000 in credits to use on our services!',
      placement: 'top',
    },
    {
      target: '[data-tour="cinematic-ads"]',
      title: 'Cinematic Ad System',
      content: 'Create stunning video ads and deploy them to Directory, Meta, Google, TikTok and more!',
      placement: 'top',
    },
  ],
  ranking: [
    {
      target: '[data-tour="ranking-tabs"]',
      title: 'Ranking System',
      content: 'Three main sections: Free Tools for quick SEO analysis, Projects to manage your campaigns, and SOPs & Guides for instructions.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="add-project"]',
      title: 'Add Project',
      content: 'Create a new project. Choose between DIY (free, self-managed) or DFY (Done For You with 7-day free trial).',
      placement: 'bottom',
    },
    {
      target: '[data-tour="take-tour"]',
      title: 'Demo Project',
      content: 'Explore all features with a demo project before creating your own. See how everything works!',
      placement: 'bottom',
    },
  ],
  team: [
    {
      target: '[data-tour="add-team-member"]',
      title: 'Add Team Members',
      content: 'Invite team members to your organization. Assign roles like Editor or Viewer to control their access.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="team-list"]',
      title: 'Team Overview',
      content: 'View and manage all your team members. Edit roles or remove members at any time.',
      placement: 'top',
    },
  ],
};

// Custom tooltip component
const TourTooltip = ({ step, currentStep, totalSteps, onNext, onPrev, onSkip, onClose, position }) => {
  const isCenter = step.placement === 'center';
  
  return createPortal(
    <div className="fixed inset-0 z-[10001] pointer-events-none">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/70 pointer-events-auto transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Tooltip */}
      <div
        className={cn(
          "absolute pointer-events-auto bg-slate-800 rounded-xl shadow-2xl border border-white/20 p-5 max-w-sm transition-all duration-300",
          isCenter && "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        )}
        style={!isCenter ? {
          top: position?.top,
          left: position?.left,
          transform: position?.transform,
        } : undefined}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* Content */}
        <div className="mb-4">
          <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
          <p className="text-gray-300 text-sm leading-relaxed">{step.content}</p>
        </div>
        
        {/* Progress */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "w-2 h-2 rounded-full transition-colors",
                  i === currentStep ? "bg-cyan-400" : i < currentStep ? "bg-cyan-400/50" : "bg-gray-600"
                )}
              />
            ))}
          </div>
          <span className="text-gray-500 text-xs">{currentStep + 1} / {totalSteps}</span>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSkip}
            className="text-gray-400 hover:text-white"
          >
            <SkipForward className="w-4 h-4 mr-1" />
            Skip Tour
          </Button>
          
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onPrev}
                className="border-white/20"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={onNext}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
            >
              {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
              {currentStep < totalSteps - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
        
        {/* Arrow indicator */}
        {!isCenter && step.placement === 'bottom' && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-slate-800 border-l border-t border-white/20 transform rotate-45" />
        )}
        {!isCenter && step.placement === 'top' && (
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-slate-800 border-r border-b border-white/20 transform rotate-45" />
        )}
      </div>
    </div>,
    document.body
  );
};

export const ProductTourProvider = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [position, setPosition] = useState(null);
  const location = useLocation();

  // Get page-specific steps
  const getPageSteps = useCallback(() => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') {
      return TOUR_STEPS.dashboard;
    } else if (path === '/ranking') {
      return TOUR_STEPS.ranking;
    } else if (path === '/team') {
      return TOUR_STEPS.team;
    }
    return TOUR_STEPS.global;
  }, [location.pathname]);

  // Check if first visit and start global tour
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenProductTour');
    if (!hasSeenTour) {
      setTimeout(() => {
        setSteps(TOUR_STEPS.global);
        setCurrentStep(0);
        setIsRunning(true);
      }, 1500);
    }
  }, []);

  // Calculate tooltip position based on target element
  useEffect(() => {
    if (!isRunning || !steps[currentStep]) return;
    
    const step = steps[currentStep];
    if (!step.target) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const element = document.querySelector(step.target);
      if (!element) {
        setPosition(null);
        return;
      }

      const rect = element.getBoundingClientRect();
      const tooltipWidth = 350;
      const tooltipHeight = 200;
      const offset = 12;

      let top, left, transform;

      if (step.placement === 'bottom') {
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2;
        transform = 'translateX(-50%)';
      } else if (step.placement === 'top') {
        top = rect.top - tooltipHeight - offset;
        left = rect.left + rect.width / 2;
        transform = 'translateX(-50%)';
      } else {
        // Default to bottom
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2;
        transform = 'translateX(-50%)';
      }

      // Keep within viewport
      if (left - tooltipWidth / 2 < 20) {
        left = tooltipWidth / 2 + 20;
      } else if (left + tooltipWidth / 2 > window.innerWidth - 20) {
        left = window.innerWidth - tooltipWidth / 2 - 20;
      }

      // Highlight target element
      element.style.position = 'relative';
      element.style.zIndex = '10000';
      element.style.boxShadow = '0 0 0 4px rgba(34, 211, 238, 0.5), 0 0 20px rgba(34, 211, 238, 0.3)';
      element.style.borderRadius = '8px';

      setPosition({ top: `${top}px`, left: `${left}px`, transform });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      // Clean up highlight
      const element = document.querySelector(step.target);
      if (element) {
        element.style.position = '';
        element.style.zIndex = '';
        element.style.boxShadow = '';
        element.style.borderRadius = '';
      }
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isRunning, currentStep, steps]);

  const startTour = useCallback(() => {
    const pageSteps = getPageSteps();
    setSteps(pageSteps);
    setCurrentStep(0);
    setIsRunning(true);
  }, [getPageSteps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleClose = () => {
    // Clean up any highlighted elements
    document.querySelectorAll('[data-tour]').forEach(el => {
      el.style.position = '';
      el.style.zIndex = '';
      el.style.boxShadow = '';
      el.style.borderRadius = '';
    });
    
    setIsRunning(false);
    setCurrentStep(0);
    localStorage.setItem('hasSeenProductTour', 'true');
  };

  return (
    <TourContext.Provider value={{ startTour, isRunning }}>
      {children}
      {/* Tour Tooltip */}
      {isRunning && steps[currentStep] && (
        <TourTooltip
          step={steps[currentStep]}
          currentStep={currentStep}
          totalSteps={steps.length}
          onNext={handleNext}
          onPrev={handlePrev}
          onSkip={handleClose}
          onClose={handleClose}
          position={position}
        />
      )}
    </TourContext.Provider>
  );
};

// Keep backward compatibility
export const ProductTour = ProductTourProvider;

export default ProductTourProvider;
